import { useCallback, useState } from 'react';
import api, { analyzeSentimentBatch } from '../../services/api.js';

/**
 * Hook para operações em lote de artigos
 * @param {Function} setArticles - Função para atualizar o estado dos artigos
 * @param {Function} showSnackbar - Função para exibir notificações
 * @param {Function} cleanupSelection - Função para limpar IDs inexistentes da seleção
 * @returns {Object} Objeto com funções de operações em lote (toggleFavoriteBulk, toggleReadBulk, etc.)
 */
const useArticleBulkActions = (setArticles, showSnackbar, cleanupSelection) => {
  // Estado de loading para ações em massa
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkActionProgress, setBulkActionProgress] = useState(null);

  // Notificação
  const notify = useCallback((message, severity = 'success') => {
    if (typeof showSnackbar === 'function') {
      showSnackbar(message, severity);
    }
  }, [showSnackbar]);

  /**
   * Função auxiliar para processar itens em lotes (batches)
   * Evita criar 1000+ requisições simultâneas
   * 
   * @param {Array} items - Array de IDs para processar
   * @param {Function} processFn - Função async que processa um item
   * @param {Object} options - Opções de configuração
   * @returns {Promise<Object>} Resultado com sucessos e falhas
   */
  const processBatch = async (items, processFn, options = {}) => {
    const {
      batchSize = 20, // Processar 20 itens por vez (padrão)
      showProgress = true,
      progressMessage = 'Processando'
    } = options;

    const results = {
      succeeded: [],
      failed: [],
      total: items.length
    };

    // Dividir em batches
    const batches = [];
    for (let i = 0; i < items.length; i += batchSize) {
      batches.push(items.slice(i, i + batchSize));
    }

    // Processar cada batch sequencialmente
    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      
      // Atualizar progresso
      if (showProgress) {
        const processed = i * batchSize + batch.length;
        const percentage = Math.round((processed / items.length) * 100);
        setBulkActionProgress({
          current: processed,
          total: items.length,
          percentage,
          message: `${progressMessage}... ${processed}/${items.length} (${percentage}%)`
        });
      }

      // Processar batch atual em paralelo
      const batchPromises = batch.map(async (item) => {
        try {
          const result = await processFn(item);
          results.succeeded.push({ item, result });
          return { success: true, item, result };
        } catch (error) {
          results.failed.push({ item, error });
          console.error(`Erro ao processar item ${item}:`, error);
          return { success: false, item, error };
        }
      });

      await Promise.all(batchPromises);

      // Pequeno delay entre batches para não sobrecarregar servidor (50ms)
      if (i < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    // Limpar progresso
    setBulkActionProgress(null);

    return results;
  };

  // Favoritar em lote com processamento em batches + OPTIMISTIC UPDATE
  const toggleFavoriteBulk = useCallback(async (selectedItems) => {
    if (selectedItems.length === 0) return false;
    setBulkActionLoading(true);
    
    // 🚀 OPTIMISTIC UPDATE: Atualizar UI IMEDIATAMENTE
    setArticles(prevArticles => prevArticles.map(article => {
      if (selectedItems.includes(article._id)) {
        return { ...article, isFavorite: !article.isFavorite };
      }
      return article;
    }));
    
    try {
      console.log('🚀 Iniciando sincronização de', selectedItems.length, 'favoritos');
      const results = await processBatch(
        selectedItems,
        async (id) => {
          console.log('📤 Enviando favorito para:', id);
          const response = await api.put(`/articles/${id}/favorite`);
          console.log('✅ Resposta recebida para:', id, response.data);
          return response;
        },
        { 
          batchSize: 20,
          showProgress: selectedItems.length > 50,
          progressMessage: 'Sincronizando favoritos'
        }
      );

      console.log('✅ Sincronização concluída:', results);
      console.log('✅ Sucessos:', results.succeeded.length);
      console.log('❌ Falhas:', results.failed.length);

      // Corrigir estado com valores reais do backend (para casos de falha)
      if (results.failed.length > 0) {
        setArticles(prevArticles => prevArticles.map(article => {
          // Reverter apenas os que falharam
          const failedItem = results.failed.find(r => r.item === article._id);
          if (failedItem) {
            return { ...article, isFavorite: !article.isFavorite }; // Reverter
          }
          return article;
        }));
      }

      // Notificar resultado
      if (results.failed.length > 0) {
        notify(`${results.succeeded.length} atualizados, ${results.failed.length} falharam`, 'warning');
      } else {
        notify(`Status de favorito alterado para ${results.succeeded.length} artigos`);
      }
      return true;
    } catch (err) {
      // Reverter TODOS em caso de erro geral
      setArticles(prevArticles => prevArticles.map(article => {
        if (selectedItems.includes(article._id)) {
          return { ...article, isFavorite: !article.isFavorite };
        }
        return article;
      }));
      console.error('Erro ao atualizar favoritos em lote:', err);
      notify('Erro ao atualizar favoritos dos artigos selecionados', 'error');
      return false;
    } finally {
      setBulkActionLoading(false);
      setBulkActionProgress(null);
    }
  }, [setArticles, notify, processBatch]);

  // Ler em lote com processamento em batches + OPTIMISTIC UPDATE
  const toggleReadBulk = useCallback(async (selectedItems) => {
    if (selectedItems.length === 0) return false;
    setBulkActionLoading(true);
    
    // 🚀 OPTIMISTIC UPDATE: Atualizar UI IMEDIATAMENTE
    setArticles(prevArticles => prevArticles.map(article => {
      if (selectedItems.includes(article._id)) {
        return { ...article, isRead: !article.isRead };
      }
      return article;
    }));
    
    try {
      const results = await processBatch(
        selectedItems,
        async (id) => await api.put(`/articles/${id}/read`),
        { 
          batchSize: 20,
          showProgress: selectedItems.length > 50,
          progressMessage: 'Atualizando leitura'
        }
      );

      // Corrigir estado com valores reais do backend (para casos de falha)
      if (results.failed.length > 0) {
        setArticles(prevArticles => prevArticles.map(article => {
          // Reverter apenas os que falharam
          const failedItem = results.failed.find(r => r.item === article._id);
          if (failedItem) {
            return { ...article, isRead: !article.isRead }; // Reverter
          }
          return article;
        }));
      }

      // Notificar resultado
      if (results.failed.length > 0) {
        notify(`${results.succeeded.length} atualizados, ${results.failed.length} falharam`, 'warning');
      } else {
        notify(`Status de leitura alterado para ${results.succeeded.length} artigos`);
      }
      return true;
    } catch (err) {
      // Reverter TODOS em caso de erro geral
      setArticles(prevArticles => prevArticles.map(article => {
        if (selectedItems.includes(article._id)) {
          return { ...article, isRead: !article.isRead };
        }
        return article;
      }));
      console.error('Erro ao atualizar status de leitura em lote:', err);
      notify('Erro ao atualizar status de leitura dos artigos selecionados', 'error');
      return false;
    } finally {
      setBulkActionLoading(false);
      setBulkActionProgress(null);
    }
  }, [setArticles, notify, processBatch]);

  // Atualizar sentimento em lote com processamento em batches
  const updateSentimentBulk = useCallback(async (selectedItems, sentiment) => {
    if (selectedItems.length === 0 || !sentiment) return false;
    setBulkActionLoading(true);
    
    try {
      const results = await processBatch(
        selectedItems,
        async (id) => await api.put(`/articles/${id}/sentiment`, { sentiment }),
        { 
          batchSize: 20,
          showProgress: selectedItems.length > 50,
          progressMessage: 'Atualizando sentimento'
        }
      );

      // Atualizar estado local
      setArticles(prevArticles => prevArticles.map(article => {
        if (results.succeeded.find(r => r.item === article._id)) {
          return { ...article, sentiment };
        }
        return article;
      }));

      // Notificar resultado
      if (results.failed.length > 0) {
        notify(`${results.succeeded.length} atualizados, ${results.failed.length} falharam`, 'warning');
      } else {
        notify(`Sentimento atualizado para ${results.succeeded.length} artigos`);
      }
      return true;
    } catch (err) {
      console.error('Erro ao atualizar sentimento em lote:', err);
      notify('Erro ao atualizar sentimento dos artigos selecionados', 'error');
      return false;
    } finally {
      setBulkActionLoading(false);
      setBulkActionProgress(null);
    }
  }, [setArticles, notify, processBatch]);

  // Adicionar tags em lote com processamento em batches
  const addTagsBulk = useCallback(async (selectedItems, selectedTagIds) => {
    if (selectedItems.length === 0 || selectedTagIds.length === 0) return false;
    setBulkActionLoading(true);
    
    try {
      const results = await processBatch(
        selectedItems,
        async (articleId) => {
          const res = await api.get(`/articles/${articleId}`);
          const article = res.data.data;
          let currentTagIds = article.tags.map(tag => typeof tag === 'object' ? tag._id : tag);
          const newTagIds = [...new Set([...currentTagIds, ...selectedTagIds])];
          return api.patch(`/articles/${articleId}/tags`, { tags: newTagIds });
        },
        { 
          batchSize: 15, // Menor porque faz 2 requisições por item (GET + PATCH)
          showProgress: selectedItems.length > 30,
          progressMessage: 'Adicionando tags'
        }
      );

      // Recarregar artigos atualizados
      if (results.succeeded.length > 0) {
        const successIds = results.succeeded.map(r => r.item);
        const updatedResults = await processBatch(
          successIds,
          async (id) => {
            const res = await api.get(`/articles/${id}`);
            return res.data.data;
          },
          { 
            batchSize: 20,
            showProgress: false
          }
        );

        setArticles(prevArticles => prevArticles.map(article => {
          const updated = updatedResults.succeeded.find(r => r.result._id === article._id);
          return updated ? updated.result : article;
        }));
      }

      // Notificar resultado
      if (results.failed.length > 0) {
        notify(`Tags adicionadas a ${results.succeeded.length} artigos, ${results.failed.length} falharam`, 'warning');
      } else {
        notify(`Tags adicionadas a ${results.succeeded.length} artigos`);
      }

      // Notificar TagSettingsContext para atualizar contagens/capa das tags
      window.dispatchEvent(new CustomEvent('tags-updated'));

      return true;
    } catch (err) {
      console.error('Erro ao adicionar tags em lote:', err);
      notify('Erro ao adicionar tags aos artigos selecionados', 'error');
      return false;
    } finally {
      setBulkActionLoading(false);
      setBulkActionProgress(null);
    }
  }, [setArticles, notify, processBatch]);

  // Excluir artigos em lote com processamento em batches
  const deleteArticlesBulk = useCallback(async (selectedItems) => {
    if (selectedItems.length === 0) return false;
    setBulkActionLoading(true);
    
    try {
      const results = await processBatch(
        selectedItems,
        async (id) => await api.delete(`/articles/${id}`),
        { 
          batchSize: 20,
          showProgress: selectedItems.length > 50,
          progressMessage: 'Excluindo artigos'
        }
      );

      // Atualizar o estado dos artigos
      setArticles(prevArticles => {
        const successIds = results.succeeded.map(r => r.item);
        const updatedArticles = prevArticles.filter(article =>
          !successIds.includes(article._id)
        );

        // Limpar IDs excluídos da seleção
        if (cleanupSelection) {
          const validIds = updatedArticles.map(article => article._id);
          cleanupSelection(validIds);
        }

        return updatedArticles;
      });

      // Notificar resultado
      if (results.failed.length > 0) {
        notify(`${results.succeeded.length} excluídos, ${results.failed.length} falharam`, 'warning');
      } else {
        notify(`${results.succeeded.length} artigos excluídos com sucesso`);
      }
      return true;
    } catch (err) {
      console.error('Erro ao excluir artigos em lote:', err);
      notify('Erro ao excluir os artigos selecionados', 'error');
      return false;
    } finally {
      setBulkActionLoading(false);
      setBulkActionProgress(null);
    }
  }, [setArticles, notify, cleanupSelection, processBatch]);

  // Adicionar artigos a uma coleção em lote
  // Esta operação JÁ está otimizada no backend (uma única requisição)
  const addToCollectionBulk = useCallback(async (selectedItems, collectionId) => {
    if (selectedItems.length === 0 || !collectionId) return false;
    setBulkActionLoading(true);
    try {
      await api.post(`/collections/${collectionId}/articles`, {
        articleIds: selectedItems
      });
      notify(`${selectedItems.length} artigos adicionados à coleção`);
      return true;
    } catch (err) {
      console.error('Erro ao adicionar artigos à coleção em lote:', err);
      notify('Erro ao adicionar artigos à coleção', 'error');
      return false;
    } finally {
      setBulkActionLoading(false);
    }
  }, [notify]);

  // Remover artigos de uma coleção em lote com processamento em batches
  const removeFromCollectionBulk = useCallback(async (selectedItems, collectionId) => {
    if (selectedItems.length === 0 || !collectionId) return false;
    setBulkActionLoading(true);
    
    try {
      const results = await processBatch(
        selectedItems,
        async (articleId) => await api.delete(`/collections/${collectionId}/articles/${articleId}`),
        { 
          batchSize: 20,
          showProgress: selectedItems.length > 50,
          progressMessage: 'Removendo da coleção'
        }
      );

      // Notificar resultado
      if (results.failed.length > 0) {
        notify(`${results.succeeded.length} removidos, ${results.failed.length} falharam`, 'warning');
      } else {
        notify(`${results.succeeded.length} artigos removidos da coleção`);
      }
      return true;
    } catch (err) {
      console.error('Erro ao remover artigos da coleção em lote:', err);
      notify('Erro ao remover artigos da coleção', 'error');
      return false;
    } finally {
      setBulkActionLoading(false);
      setBulkActionProgress(null);
    }
  }, [notify, processBatch]);

  // Remover artigos de múltiplas coleções em lote
  // Esta operação JÁ está otimizada no backend (uma única requisição)
  const removeFromCollectionsBulk = useCallback(async (selectedItems, collectionIds) => {
    if (selectedItems.length === 0 || collectionIds.length === 0) return false;
    setBulkActionLoading(true);
    try {
      const response = await api.post('/collections/bulk-remove-articles', {
        articleIds: selectedItems,
        collectionIds
      });

      if (response.data.success) {
        notify(`Artigos removidos de ${response.data.modifiedCount} coleções`);
        return true;
      } else {
        notify(response.data.error || 'Erro ao remover de coleções', 'error');
        return false;
      }
    } catch (err) {
      console.error('Erro ao remover artigos de coleções em lote:', err);
      notify('Erro ao remover artigos das coleções selecionadas', 'error');
      return false;
    } finally {
      setBulkActionLoading(false);
    }
  }, [notify]);

  // Remover tags em lote
  // Esta operação JÁ está otimizada no backend (uma única requisição)
  const removeTagsBulk = useCallback(async (selectedItems, tagIds) => {
    if (selectedItems.length === 0 || tagIds.length === 0) return false;
    setBulkActionLoading(true);
    try {
      const response = await api.post('/articles/bulk-remove-tags', {
        articleIds: selectedItems,
        tagIds
      });

      if (response.data.success) {
        // Atualizar artigos localmente removendo as tags
        setArticles(prevArticles => prevArticles.map(article => {
          if (selectedItems.includes(article._id)) {
            const updatedTags = article.tags?.filter(tag => {
              const tagId = typeof tag === 'object' ? tag._id : tag;
              return !tagIds.includes(tagId);
            }) || [];
            return { ...article, tags: updatedTags };
          }
          return article;
        }));

        notify(`Tags removidas de ${response.data.modifiedCount} artigos`);

        // Notificar TagSettingsContext para atualizar contagens/capa das tags
        window.dispatchEvent(new CustomEvent('tags-updated'));

        return true;
      } else {
        notify(response.data.error || 'Erro ao remover tags', 'error');
        return false;
      }
    } catch (err) {
      console.error('Erro ao remover tags em lote:', err);
      notify('Erro ao remover tags dos artigos selecionados', 'error');
      return false;
    } finally {
      setBulkActionLoading(false);
    }
  }, [setArticles, notify]);

  // Editar campos em lote
  const updateFieldsBulk = useCallback(async (selectedItems, patch) => {
    if (selectedItems.length === 0 || !patch || Object.keys(patch).length === 0) return false;

    const hasExposure = Object.prototype.hasOwnProperty.call(patch, 'exposure');
    const { exposure, ...articlePatch } = patch;
    const articlePatchKeys = Object.keys(articlePatch);

    setBulkActionLoading(true);

    try {
      const results = await processBatch(
        selectedItems,
        async (id) => {
          let response = null;

          if (articlePatchKeys.length > 0) {
            response = await api.put(`/articles/${id}`, articlePatch);
          }

          if (hasExposure) {
            response = await api.put(`/articles/${id}/exposure`, { exposure });
          }

          return response;
        },
        {
          batchSize: hasExposure ? 10 : 20,
          showProgress: selectedItems.length > 30,
          progressMessage: 'Editando artigos'
        }
      );

      const succeededIds = results.succeeded.map(result => result.item);
      const localPatch = {
        ...articlePatch,
        ...(hasExposure ? { exposure, exposureManual: true } : {})
      };

      setArticles(prevArticles => prevArticles.map(article => (
        succeededIds.includes(article._id)
          ? { ...article, ...localPatch }
          : article
      )));

      if (results.failed.length > 0) {
        notify(`${results.succeeded.length} editados, ${results.failed.length} falharam`, 'warning');
      } else {
        notify(`${results.succeeded.length} artigos editados com sucesso`);
      }

      return results.succeeded.length > 0;
    } catch (err) {
      console.error('Erro ao editar campos em lote:', err);
      notify('Erro ao editar campos dos artigos selecionados', 'error');
      return false;
    } finally {
      setBulkActionLoading(false);
      setBulkActionProgress(null);
    }
  }, [setArticles, notify, processBatch]);

  // Analisar sentimento com IA em lote (usa API do Gemini)
  const analyzeSentimentBulk = useCallback(async (selectedItems) => {
    if (selectedItems.length === 0) return false;
    if (selectedItems.length > 100) {
      notify('Máximo de 100 artigos por vez. Selecione menos artigos.', 'warning');
      return false;
    }

    setBulkActionLoading(true);
    try {
      const response = await analyzeSentimentBatch(selectedItems, true, 500);
      const { results, success, errors } = response.data;

      // Atualizar artigos localmente com os resultados da análise
      setArticles(prevArticles => prevArticles.map(article => {
        const result = results.find(r => r.articleId === article._id);
        if (result && !result.error) {
          return {
            ...article,
            sentiment: result.sentiment,
            sentimentConfidence: result.confidence,
            sentimentTone: result.tone,
            sentimentBrandMention: result.brandMention,
            sentimentSummary: result.summary,
            sentimentAnalyzedAt: result.analyzedAt
          };
        }
        return article;
      }));

      if (errors > 0) {
        notify(`Análise concluída: ${success} sucesso, ${errors} erro(s)`, 'warning');
      } else {
        notify(`Sentimento analisado com IA para ${success} artigos`);
      }
      return true;
    } catch (err) {
      console.error('Erro ao analisar sentimento em lote:', err);
      notify('Erro ao analisar sentimento: ' + (err.response?.data?.message || err.message), 'error');
      return false;
    } finally {
      setBulkActionLoading(false);
    }
  }, [setArticles, notify]);

  return {
    toggleFavoriteBulk,
    toggleReadBulk,
    updateSentimentBulk,
    analyzeSentimentBulk,
    addTagsBulk,
    removeTagsBulk,
    updateFieldsBulk,
    deleteArticlesBulk,
    addToCollectionBulk,
    removeFromCollectionBulk,
    removeFromCollectionsBulk,
    bulkActionLoading,
    bulkActionProgress // Novo: progresso da operação em lote
  };
};

export default useArticleBulkActions;
